import React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import Iconify from 'src/components/iconify';
import { formatAmount } from 'src/utils/formatNumber';
import { config } from 'src/config-global';
import { getConfirmationProgress } from 'src/constants/transactionStatus';
import StatusBadge from './StatusBadge';
import { shortAddress, formatDateMD } from './transactionUtils';

function getConfirmText(tx) {
  const progress = getConfirmationProgress(tx);
  if (!progress) return null;
  return `(${progress})`;
}

function getHoverShadow(theme) {
  if (theme.palette.mode === 'light') return '0 10px 24px rgba(16, 24, 40, 0.06)';
  return '0 10px 24px rgba(0, 0, 0, 0.22)';
}

function getCardHoverSx(theme, redirect) {
  if (!redirect) return undefined;

  return {
    transform: 'translateY(-1px)',
    boxShadow: getHoverShadow(theme),
  };
}

function TransactionListMobile({ transactions, redirect }) {
  const theme = useTheme();

  const copyToClipboard = async (text, e) => {
    e?.stopPropagation?.();
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // no-op
    }
  };

  const stop = (e) => e.stopPropagation();

  function renderConfirmText(confirmText) {
    if (!confirmText) return null;

    return (
      <Typography variant="caption" color="text.secondary">
        {confirmText}
      </Typography>
    );
  }

  function renderTokenOut(tx) {
    if (!tx.tokenOut) return null;

    return (
      <>
        <Box sx={{ color: 'text.secondary', display: 'grid', placeItems: 'center' }}>
          <Iconify icon="mingcute:arrow-right-line" width={18} />
        </Box>

        <Typography fontWeight={800} variant="body1" sx={{ lineHeight: 1.15 }}>
          {formatAmount(tx.amountOut)} {tx.tokenOut?.toUpperCase()}
        </Typography>
      </>
    );
  }

  function renderTxHash(tx) {
    if (!tx.txHashOut) return null;

    return (
      <>
        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Tx Hash
          </Typography>

          <Button
            onClick={stop}
            href={`${config.etherscanEndPoint}/tx/${tx.txHashOut}`}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{
              minWidth: 0,
              p: 0.5,
              borderRadius: 2,
              color: 'text.primary',
            }}
            endIcon={<Iconify icon="solar:arrow-right-up-bold" width={14} />}
          >
            <Typography variant="caption" fontFamily="monospace">
              {shortAddress(tx.txHashOut)}
            </Typography>
          </Button>
        </Stack>
      </>
    );
  }

  return (
    <Stack spacing={1.5}>
      {(transactions || []).map((tx) => {
        const confirmText = getConfirmText(tx);

        return (
          <Paper
            key={tx._id}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              transition: theme.transitions.create(['border-color', 'transform', 'box-shadow']),
              '&:hover': getCardHoverSx(theme, redirect),
            }}
          >
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1.5}
            >
              <Stack spacing={0.5}>
                <StatusBadge status={tx.status} size="md" />
                {renderConfirmText(confirmText)}
              </Stack>

              <Stack alignItems="flex-end" spacing={0.75}>
                <Typography variant="caption" color="text.secondary">
                  {formatDateMD(tx.createdAt)}
                </Typography>

                {redirect && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      stop(e);
                      redirect(tx);
                    }}
                    startIcon={<Iconify icon="solar:eye-bold" width={16} />}
                    sx={{
                      minHeight: 30,
                      px: 1.25,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                    }}
                  >
                    View
                  </Button>
                )}
              </Stack>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1.5, flexWrap: 'wrap' }}
            >
              <Typography fontWeight={800} variant="body1" sx={{ lineHeight: 1.15 }}>
                {formatAmount(tx.amountIn)} {tx.tokenIn?.toUpperCase()}
              </Typography>

              {renderTokenOut(tx)}
            </Stack>

            <Stack spacing={0.75}>
              <RowLabel
                label="From"
                value={shortAddress(tx.from)}
                onClick={(e) => copyToClipboard(tx.from, e)}
              />
              <RowLabel
                label="To"
                value={shortAddress(tx.to)}
                onClick={(e) => copyToClipboard(tx.to, e)}
              />
            </Stack>

            {renderTxHash(tx)}
          </Paper>
        );
      })}
    </Stack>
  );
}

function getRowLabelBg(theme) {
  if (theme.palette.mode === 'dark') return alpha(theme.palette.text.primary, 0.06);
  return alpha(theme.palette.text.primary, 0.04);
}

function RowLabel({ label, value, onClick }) {
  const theme = useTheme();

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      <Button
        onClick={onClick}
        size="small"
        sx={{
          minWidth: 0,
          px: 0.75,
          py: 0.4,
          borderRadius: 2,
          color: 'text.primary',
          bgcolor: getRowLabelBg(theme),
        }}
      >
        <Typography variant="caption" fontFamily="monospace">
          {value}
        </Typography>
        <Box sx={{ ml: 0.5, color: 'text.secondary', display: 'grid', placeItems: 'center' }}>
          <Iconify icon="solar:copy-bold" width={14} />
        </Box>
      </Button>
    </Stack>
  );
}

RowLabel.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

TransactionListMobile.propTypes = {
  redirect: PropTypes.func,
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      amountIn: PropTypes.string.isRequired,
      amountOut: PropTypes.string,
      tokenIn: PropTypes.string,
      tokenOut: PropTypes.string,
      createdAt: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      from: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
      txHashOut: PropTypes.string,
      txHashIn: PropTypes.string,
      depositConfirmations: PropTypes.number,
      payoutConfirmations: PropTypes.number,
    })
  ).isRequired,
};

export default TransactionListMobile;
